import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useWallet } from '@solana/wallet-adapter-react'
import { api } from '../services/api'
import { InvestmentStatus, Investment } from '../types'
import { Wallet, ExternalLink, Tag, X, ToggleLeft, ToggleRight, Sparkles, Clock } from 'lucide-react'
import { useClaimUSDT, useClaimTAKARA } from '../hooks/useInvestmentActions'
import { useCancelListing } from '../hooks/useMarketplace'
import ListNFTModal from '../components/marketplace/ListNFTModal'
import TaxPreviewModal from '../components/TaxPreviewModal'
import ReferralSection from '../components/referral/ReferralSection'
import MintNFTButton from '../components/MintNFTButton'
import { toast } from 'sonner'
import { QUERY_CONFIG } from '../hooks/useQueries'
import { useConfig } from '../contexts/ConfigContext'
import { BLOCKCHAIN_CONFIG } from '../config/app.config'

/** Format remaining seconds as MM:SS */
function formatCountdown(expiresAt: string): string | null {
  const remaining = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000))
  if (remaining <= 0) return null
  const m = Math.floor(remaining / 60)
  const s = remaining % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

/** Hook that ticks every second while any lock is active */
function useClaimCountdown(investments: Investment[] | undefined) {
  const [, setTick] = useState(0)
  const hasLock = investments?.some(inv =>
    inv.claimLockedUntil?.REWARD_USDT || inv.claimLockedUntil?.REWARD_TAKARA
  )
  useEffect(() => {
    if (!hasLock) return
    const id = setInterval(() => setTick(t => t + 1), 1000)
    return () => clearInterval(id)
  }, [hasLock])
}

export default function PortfolioPage() {
  const { t } = useTranslation(['portfolio', 'common'])
  const { publicKey } = useWallet()
  const isAuthenticated = api.isAuthenticated()
  const queryClient = useQueryClient()
  const config = useConfig()
  const [statusFilter, setStatusFilter] = useState<InvestmentStatus | 'ALL'>('ALL')
  const [selectedInvestment, setSelectedInvestment] = useState<Investment | null>(null)
  const [isListModalOpen, setIsListModalOpen] = useState(false)
  const [taxPreviewInvestment, setTaxPreviewInvestment] = useState<Investment | null>(null)
  const [isTaxPreviewOpen, setIsTaxPreviewOpen] = useState(false)
  const [usdtPreviewInvestment, setUsdtPreviewInvestment] = useState<Investment | null>(null)
  const [isUsdtPreviewOpen, setIsUsdtPreviewOpen] = useState(false)
  const claimUSDT = useClaimUSDT()
  const claimTAKARA = useClaimTAKARA()
  const cancelListing = useCancelListing()

  // Instant Sale toggle mutation
  const toggleInstantSaleMutation = useMutation({
    mutationFn: ({ investmentId, enabled }: { investmentId: string; enabled: boolean }) =>
      api.toggleInstantSale(investmentId, enabled),
    onSuccess: (_, variables) => {
      toast.success(variables.enabled ? t('portfolio:toast.instantSaleEnabled') : t('portfolio:toast.instantSaleDisabled'))
      queryClient.invalidateQueries({ queryKey: ['myInvestments'] })
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || t('portfolio:toast.instantSaleFailed'))
    },
  })

  const { data: userResponse } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => api.getCurrentUser(),
    enabled: isAuthenticated,
    ...QUERY_CONFIG.currentUser,
  })

  const { data: investmentsResponse, isLoading, refetch: refetchInvestments } = useQuery({
    queryKey: ['myInvestments', userResponse?.data?.id, statusFilter],
    queryFn: () =>
      api.getMyInvestments(statusFilter === 'ALL' ? undefined : statusFilter, false),
    enabled: isAuthenticated && !!userResponse?.data?.id,
    ...QUERY_CONFIG.investments,
  })

  const investments = investmentsResponse?.data || []

  // Tick countdown timer for locked claims
  useClaimCountdown(investments)

  // Fetch user's listings
  const { data: listingsResponse } = useQuery({
    queryKey: ['myListings'],
    queryFn: () => api.getMyListings(),
    enabled: isAuthenticated,
    ...QUERY_CONFIG.marketplace,
  })

  const myListings = listingsResponse?.data || []

  // Check if investment is listed (only ACTIVE listings)
  const isListed = (investmentId: string) => {
    return myListings.some((listing: any) =>
      listing.investmentId === investmentId && listing.status === 'ACTIVE'
    )
  }

  // Get listing data for investment
  const getListing = (investmentId: string) => {
    return myListings.find((l: any) =>
      l.investmentId === investmentId && l.status === 'ACTIVE'
    )
  }

  // Get listing ID for investment
  const getListingId = (investmentId: string) => {
    const listing = getListing(investmentId)
    return listing?.id
  }

  // Get listing price for investment
  const getListingPrice = (investmentId: string) => {
    const listing = getListing(investmentId)
    return listing?.priceUSDT
  }

  const handleListClick = (investment: Investment) => {
    setSelectedInvestment(investment)
    setIsListModalOpen(true)
  }

  const handleCancelListing = async (investmentId: string) => {
    const listingId = getListingId(investmentId)
    if (listingId) {
      await cancelListing.mutateAsync(listingId)
    }
  }

  const handleTakaraClaimClick = (investment: Investment) => {
    if (!publicKey) {
      toast.error(t('portfolio:claims.connectWalletFirst'))
      return
    }
    setTaxPreviewInvestment(investment)
    setIsTaxPreviewOpen(true)
  }

  const handleConfirmTakaraClaim = async () => {
    if (taxPreviewInvestment) {
      try {
        await claimTAKARA.mutateAsync(taxPreviewInvestment.id)
      } catch (err: any) {
        toast.error(err.message || 'Failed to claim TAKARA')
      } finally {
        setIsTaxPreviewOpen(false)
        setTaxPreviewInvestment(null)
      }
    }
  }

  const handleUsdtClaimClick = (investment: Investment) => {
    if (!publicKey) {
      toast.error(t('portfolio:claims.connectWalletFirst'))
      return
    }
    setUsdtPreviewInvestment(investment)
    setIsUsdtPreviewOpen(true)
  }

  const handleConfirmUsdtClaim = async () => {
    if (usdtPreviewInvestment) {
      try {
        await claimUSDT.mutateAsync(usdtPreviewInvestment.id)
      } catch (err: any) {
        toast.error(err.message || 'Failed to claim USDT')
      } finally {
        setIsUsdtPreviewOpen(false)
        setUsdtPreviewInvestment(null)
      }
    }
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <Wallet className="h-16 w-16 text-gray-500 mx-auto" />
          <h2 className="text-2xl font-bold text-white">{t('portfolio:loginRequired.title')}</h2>
          <p className="text-gray-400">{t('portfolio:loginRequired.message')}</p>
        </div>
      </div>
    )
  }

  const statusColors: Record<InvestmentStatus, string> = {
    PENDING: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    PENDING_USDT: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    PENDING_VERIFICATION: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    PENDING_TOKENS: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    ACTIVE: 'bg-green-500/20 text-green-400 border-green-500/30',
    COMPLETED: 'bg-teal-500/20 text-teal-400 border-teal-500/30',
    WITHDRAWN: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    SOLD: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    CANCELLED: 'bg-red-500/20 text-red-400 border-red-500/30',
    VERIFICATION_FAILED: 'bg-red-500/20 text-red-400 border-red-500/30',
  }

  // Status display labels with user-friendly text
  const statusLabels: Record<InvestmentStatus, string> = {
    PENDING: t('portfolio:status.PENDING'),
    PENDING_USDT: t('portfolio:status.PENDING_USDT'),
    PENDING_VERIFICATION: t('portfolio:status.PENDING_VERIFICATION'),
    PENDING_TOKENS: t('portfolio:status.PENDING_TOKENS'),
    ACTIVE: t('portfolio:status.ACTIVE'),
    COMPLETED: t('portfolio:status.COMPLETED'),
    WITHDRAWN: t('portfolio:status.WITHDRAWN'),
    SOLD: t('portfolio:status.SOLD'),
    CANCELLED: t('portfolio:status.CANCELLED'),
    VERIFICATION_FAILED: t('portfolio:status.VERIFICATION_FAILED'),
  }

  return (
    <div className="min-h-screen py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">{t('portfolio:title')}</h1>
          <p className="text-gray-400">{t('portfolio:subtitle')}</p>
        </div>

        {/* Referral Section - only shown for users who can invite */}
        <div className="mb-8">
          <ReferralSection />
        </div>

        {/* Status Filter */}
        <div className="bg-background-card rounded-xl p-6 mb-8 border border-green-900/20">
          <label className="block text-sm font-medium text-gray-300 mb-3">
            {t('portfolio:filter.label')}
          </label>
          <div className="flex flex-wrap gap-2">
            {['ALL', ...Object.values(InvestmentStatus)].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status as InvestmentStatus | 'ALL')}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  statusFilter === status
                    ? 'bg-gold-500 text-background-primary'
                    : 'bg-background-elevated text-gray-300 hover:bg-green-900/20'
                }`}
              >
                {status === 'ALL' ? t('portfolio:filter.all') : statusLabels[status as InvestmentStatus] || status}
              </button>
            ))}
          </div>
        </div>

        {/* Investments List */}
        {isLoading ? (
          <div className="text-center py-20">
            <div className="loading-spinner mx-auto mb-4"></div>
            <p className="text-gray-400">{t('portfolio:loading')}</p>
          </div>
        ) : investments.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-400 mb-4">{t('portfolio:noInvestments')}</p>
            <a href="/vaults" className="btn-gold inline-block px-6 py-2 rounded-lg">
              {t('portfolio:browseVaults')}
            </a>
          </div>
        ) : (
          <div className="space-y-4">
            {investments.map((investment) => (
              <div
                key={investment.id}
                className="bg-background-card rounded-xl p-6 border border-green-900/20 hover:border-gold-500/30 transition-all"
              >
                {/* Header */}
                <div className="flex flex-wrap justify-between items-start gap-4 mb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`tier-${(investment.vaultTier || '').toLowerCase()} inline-block`}>
                        {investment.vaultTier}
                      </div>
                      <div
                        className={`px-3 py-1 rounded-lg text-xs font-medium border ${
                          statusColors[investment.status]
                        }`}
                      >
                        {statusLabels[investment.status] || investment.status}
                      </div>
                      {isListed(investment.id) && (
                        <div className="px-3 py-1 rounded-lg text-xs font-medium bg-purple-500/30 text-purple-400 border border-purple-500/30 animate-pulse">
                          {t('portfolio:badges.forSale', { price: getListingPrice(investment.id)?.toLocaleString() })}
                        </div>
                      )}
                      {investment.isOriginalOwner === false && (
                        <div className="px-3 py-1 rounded-lg text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/30">
                          {t('portfolio:badges.purchasedNFT')}
                        </div>
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-1">
                      {investment.vaultName}
                    </h3>
                    {/* Show dates only for ACTIVE/COMPLETED investments */}
                    {investment.status === 'ACTIVE' || investment.status === 'COMPLETED' ? (
                      <p className="text-sm text-gray-400">
                        {new Date(investment.startDate).toLocaleDateString()} -{' '}
                        {new Date(investment.endDate).toLocaleDateString()}
                      </p>
                    ) : (
                      <p className="text-sm text-yellow-400">
                        {t('portfolio:badges.pendingActivation')}
                      </p>
                    )}
                  </div>

                  {investment.nftMintAddress && (
                    <a
                      href={`https://solscan.io/token/${investment.nftMintAddress}${BLOCKCHAIN_CONFIG.solana.network === 'devnet' ? '?cluster=devnet' : ''}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-gold-500 hover:text-gold-400 transition-colors"
                    >
                      {t('portfolio:nft.viewWexel')}
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>

                {/* Mint NFT Button - for active investments ready to mint */}
                {investment.status === 'ACTIVE' && investment.nftReadyToMint && !investment.isNFTMinted && !investment.nftMintAddress && (
                  <div className="mb-6 p-4 bg-gradient-to-r from-gold-500/10 to-purple-500/10 border border-gold-500/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-3">
                      <Sparkles className="h-5 w-5 text-gold-400" />
                      <span className="text-sm font-medium text-gold-400">{t('portfolio:nft.readyToMint')}</span>
                    </div>
                    <MintNFTButton
                      investmentId={investment.id}
                      vaultName={investment.vaultName}
                      usdtAmount={investment.usdtAmount}
                      mintLockedUntil={investment.mintLockedUntil}
                      onSuccess={() => {
                        queryClient.invalidateQueries({ queryKey: ['myInvestments'] })
                        refetchInvestments() // Immediate refetch to update UI
                      }}
                    />
                  </div>
                )}

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
                  <div className="bg-background-elevated rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">{t('portfolio:stats.investment')}</div>
                    <div className="text-sm font-semibold text-gold-500">
                      ${investment.usdtAmount.toLocaleString()} USDT
                    </div>
                    {investment.laikaBoost && investment.laikaBoost.laikaAmount > 0 && (
                      <div className="text-xs text-blue-400 mt-1">
                        {investment.laikaBoost.laikaAmount.toLocaleString()} LAIKA
                      </div>
                    )}
                    {investment.takaraBoost && investment.takaraBoost.takaraAmount > 0 && (
                      <div className="text-xs text-green-400 mt-1">
                        {investment.takaraBoost.takaraAmount.toLocaleString()} TAKARA
                      </div>
                    )}
                  </div>

                  <div className="bg-background-elevated rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">{t('portfolio:stats.takaraLocked')}</div>
                    <div className="text-lg font-semibold text-white">
                      {investment.takaraLocked.toLocaleString()}
                    </div>
                  </div>

                  <div className="bg-background-elevated rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">{t('portfolio:stats.finalUsdtAPY')}</div>
                    <div className="text-lg font-semibold text-gold-500">
                      {investment.finalAPY}%
                    </div>
                  </div>

                  <div className="bg-background-elevated rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">{t('portfolio:stats.totalEarned')}</div>
                    <div className="text-lg font-semibold text-green-400">
                      ${investment.totalEarnedUSDT.toFixed(2)}
                    </div>
                  </div>

                  <div className="bg-background-elevated rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">{t('portfolio:stats.totalMined')}</div>
                    <div className="text-lg font-semibold text-green-400">
                      {investment.totalMinedTAKARA.toFixed(2)}
                    </div>
                  </div>

                  <div className="bg-background-elevated rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">{t('portfolio:stats.status')}</div>
                    <div className="text-sm font-medium text-white">
                      {statusLabels[investment.status] || investment.status}
                    </div>
                  </div>
                </div>

                {/* Verification Status Warning */}
                {(investment.status === InvestmentStatus.PENDING_VERIFICATION ||
                  investment.status === InvestmentStatus.VERIFICATION_FAILED) && (
                  <div className={`rounded-lg p-4 mb-6 ${
                    investment.status === InvestmentStatus.PENDING_VERIFICATION
                      ? 'bg-blue-500/10 border border-blue-500/30'
                      : 'bg-red-500/10 border border-red-500/30'
                  }`}>
                    <div className={`text-sm font-medium mb-2 ${
                      investment.status === InvestmentStatus.PENDING_VERIFICATION
                        ? 'text-blue-400'
                        : 'text-red-400'
                    }`}>
                      {investment.status === InvestmentStatus.PENDING_VERIFICATION
                        ? t('portfolio:verification.inProgress')
                        : t('portfolio:verification.failed')}
                    </div>
                    <p className="text-xs text-gray-400">
                      {investment.status === InvestmentStatus.PENDING_VERIFICATION
                        ? t('portfolio:verification.inProgressMessage')
                        : t('portfolio:verification.failedMessage')}
                    </p>
                  </div>
                )}

                {/* LAIKA Boost Info */}
                {investment.laikaBoost && (
                  <div className="bg-gradient-laika/10 border border-laika-purple/30 rounded-lg p-4 mb-6">
                    <div className="text-sm text-laika-purple font-medium mb-2">
                      {t('portfolio:boost.laikaActive')}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">{t('portfolio:boost.laikaAmount')}</div>
                        <div className="text-white font-semibold">
                          ${investment.laikaBoost.laikaAmount.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">{t('portfolio:boost.additionalAPY')}</div>
                        <div className="text-laika-green font-semibold">
                          +{investment.laikaBoost.additionalAPY}%
                        </div>
                      </div>
                    </div>
                    {investment.laikaBoost.isReturned && (
                      <div className="mt-2 text-sm text-green-400">{t('portfolio:boost.returned', { token: 'LAIKA' })}</div>
                    )}
                  </div>
                )}

                {/* TAKARA Boost Info - v2.2 */}
                {investment.takaraBoost && (
                  <div className="bg-green-900/10 border border-green-500/30 rounded-lg p-4 mb-6">
                    <div className="text-sm text-green-400 font-medium mb-2">
                      {t('portfolio:boost.takaraActive')}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">{t('portfolio:boost.takaraAmount')}</div>
                        <div className="text-white font-semibold">
                          {investment.takaraBoost.takaraAmount.toLocaleString()} TAKARA
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">{t('portfolio:boost.valueUSD')}</div>
                        <div className="text-white font-semibold">
                          ${investment.takaraBoost.takaraValueUSD.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">{t('portfolio:boost.additionalAPY')}</div>
                        <div className="text-green-400 font-semibold">
                          +{investment.takaraBoost.additionalAPY}%
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 text-xs text-gray-400">
                      {t('portfolio:boost.boostPercentage', { percentage: investment.takaraBoost.boostPercentage, maxAllowed: investment.takaraBoost.maxAllowedUSD })}
                    </div>
                    {investment.takaraBoost.isReturned && (
                      <div className="mt-2 text-sm text-green-400">{t('portfolio:boost.returned', { token: 'TAKARA' })}</div>
                    )}
                  </div>
                )}

                {/* Instant Sale Info - v2.2 */}
                {investment.status === 'ACTIVE' && investment.instantSalePrice && (
                  <div className="bg-yellow-900/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm text-yellow-400 font-medium">
                        {t('portfolio:instantSale.title', { status: investment.isInstantSaleEnabled ? t('portfolio:instantSale.enabled') : t('portfolio:instantSale.disabled') })}
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        investment.isInstantSaleEnabled
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {investment.isInstantSaleEnabled ? t('portfolio:instantSale.active') : t('portfolio:instantSale.inactive')}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">{t('portfolio:instantSale.price')}</div>
                        <div className="text-white font-semibold">
                          ${investment.instantSalePrice.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">{t('portfolio:instantSale.originalValue')}</div>
                        <div className="text-gray-300">
                          ${investment.usdtAmount.toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 text-xs text-yellow-400">
                      {t('portfolio:instantSale.note')}
                    </div>
                  </div>
                )}

                {/* Management Actions for Active Investments - v2.2 */}
                {investment.status === 'ACTIVE' && !config.referral.disableInstantSale && (
                  <div className="bg-background-elevated rounded-lg p-4 mb-6">
                    <div className="text-sm font-semibold text-white mb-4">{t('portfolio:management.title')}</div>
                    {/* Instant Sale Toggle */}
                    <button
                      onClick={() => toggleInstantSaleMutation.mutate({
                        investmentId: investment.id,
                        enabled: !investment.isInstantSaleEnabled
                      })}
                      disabled={toggleInstantSaleMutation.isPending}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium text-sm transition-colors ${
                        investment.isInstantSaleEnabled
                          ? 'bg-yellow-600 hover:bg-yellow-700 text-white'
                          : 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {investment.isInstantSaleEnabled ? (
                        <ToggleRight className="h-5 w-5" />
                      ) : (
                        <ToggleLeft className="h-5 w-5" />
                      )}
                      {toggleInstantSaleMutation.isPending
                        ? t('portfolio:management.processing')
                        : investment.isInstantSaleEnabled
                        ? t('portfolio:management.disableInstantSale')
                        : t('portfolio:management.enableInstantSale')}
                    </button>
                  </div>
                )}

                {/* List / Cancel Listing Actions */}
                {investment.status === 'ACTIVE' && !config.marketplace.disableMarketplaceSale && (
                  <div className="border-t border-green-900/20 pt-4">
                    {isListed(investment.id) ? (
                      <button
                        onClick={() => handleCancelListing(investment.id)}
                        disabled={cancelListing.isPending}
                        className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white py-2.5 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2"
                      >
                        <X className="h-4 w-4" />
                        {cancelListing.isPending ? t('portfolio:marketplace.cancelling') : t('portfolio:marketplace.cancelListing')}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleListClick(investment)}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2.5 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2"
                      >
                        <Tag className="h-4 w-4" />
                        {t('portfolio:marketplace.listForSale')}
                      </button>
                    )}
                  </div>
                )}

                {/* Listed on Marketplace Warning */}
                {isListed(investment.id) && (
                  <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-2 text-purple-400">
                      <Tag className="h-5 w-5" />
                      <span className="font-medium">{t('portfolio:marketplace.listedTitle')}</span>
                    </div>
                    <p className="text-sm text-gray-400 mt-2">
                      {t('portfolio:marketplace.listedMessage', { price: getListingPrice(investment.id)?.toLocaleString() })}
                    </p>
                  </div>
                )}

                {/* Pending Claims — show if NFT is minted AND (has available reward OR has active claim lock) */}
                {investment.isNFTMinted && (
                  investment.availableRewardUSDT > 0 ||
                  investment.availableRewardTAKARA > 0 ||
                  investment.claimLockedUntil?.REWARD_USDT ||
                  investment.claimLockedUntil?.REWARD_TAKARA
                ) && (
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* USDT Claim — available or locked */}
                    {(investment.availableRewardUSDT > 0 || investment.claimLockedUntil?.REWARD_USDT) && (
                      <div className="bg-background-elevated rounded-lg p-4 flex flex-col">
                        <div className="text-sm text-gray-400 mb-2">{t('portfolio:claims.pendingUSDT')}</div>
                        {investment.claimLockedUntil?.REWARD_USDT && formatCountdown(investment.claimLockedUntil.REWARD_USDT) ? (
                          <>
                            <div className="flex items-center gap-2 text-yellow-400 mb-3">
                              <Clock className="h-4 w-4" />
                              <span className="text-sm font-medium">{t('portfolio:claims.claimInProgress')}</span>
                            </div>
                            <div className="text-xs text-gray-400 mb-2">
                              {t('portfolio:claims.lockExpiresIn', { time: formatCountdown(investment.claimLockedUntil.REWARD_USDT) })}
                            </div>
                            <button disabled className="btn-gold w-full py-2 rounded-lg font-medium text-sm opacity-50 cursor-not-allowed mt-auto">
                              {t('portfolio:claims.locked')}
                            </button>
                          </>
                        ) : investment.availableRewardUSDT > 0 ? (
                          <>
                            <div className="text-xl font-bold text-gold-500 mb-3">
                              ${investment.availableRewardUSDT.toFixed(2)}
                            </div>
                            <button
                              onClick={() => handleUsdtClaimClick(investment)}
                              disabled={claimUSDT.isPending || isListed(investment.id)}
                              className="btn-gold w-full py-2 rounded-lg font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed mt-auto"
                              title={isListed(investment.id) ? t('portfolio:claims.cannotClaimWhileListed') : ''}
                            >
                              {claimUSDT.isPending ? t('portfolio:claims.claiming') : isListed(investment.id) ? t('portfolio:claims.listedCannotClaim') : t('portfolio:claims.claimUSDT')}
                            </button>
                          </>
                        ) : null}
                      </div>
                    )}

                    {/* TAKARA Claim — available or locked */}
                    {(investment.availableRewardTAKARA > 0 || investment.claimLockedUntil?.REWARD_TAKARA) && (
                      <div className="bg-background-elevated rounded-lg p-4 flex flex-col">
                        <div className="text-sm text-gray-400 mb-2">{t('portfolio:claims.pendingTAKARA')}</div>
                        {investment.claimLockedUntil?.REWARD_TAKARA && formatCountdown(investment.claimLockedUntil.REWARD_TAKARA) ? (
                          <>
                            <div className="flex items-center gap-2 text-yellow-400 mb-3">
                              <Clock className="h-4 w-4" />
                              <span className="text-sm font-medium">{t('portfolio:claims.claimInProgress')}</span>
                            </div>
                            <div className="text-xs text-gray-400 mb-2">
                              {t('portfolio:claims.lockExpiresIn', { time: formatCountdown(investment.claimLockedUntil.REWARD_TAKARA) })}
                            </div>
                            <button disabled className="bg-green-600 opacity-50 cursor-not-allowed text-white w-full py-2 rounded-lg font-medium text-sm mt-auto">
                              {t('portfolio:claims.locked')}
                            </button>
                          </>
                        ) : investment.availableRewardTAKARA > 0 ? (
                          <>
                            <div className="text-xl font-bold text-green-400 mb-3">
                              {investment.availableRewardTAKARA.toFixed(2)}
                            </div>
                            {!config.referral.disableClaimTakara && (
                              <>
                                <div className="text-xs text-yellow-400 mb-2">
                                  {t('portfolio:claims.taxWarning')}
                                </div>
                                <button
                                  onClick={() => handleTakaraClaimClick(investment)}
                                  disabled={claimTAKARA.isPending || isListed(investment.id)}
                                  className="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white w-full py-2 rounded-lg font-medium text-sm transition-colors mt-auto"
                                  title={isListed(investment.id) ? t('portfolio:claims.cannotClaimWhileListed') : ''}
                                >
                                  {claimTAKARA.isPending ? t('portfolio:claims.claiming') : isListed(investment.id) ? t('portfolio:claims.listedCannotClaim') : t('portfolio:claims.claimTAKARA')}
                                </button>
                              </>
                            )}
                          </>
                        ) : null}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* List Wexel Modal */}
      {selectedInvestment && (
        <ListNFTModal
          isOpen={isListModalOpen}
          onClose={() => {
            setIsListModalOpen(false)
            setSelectedInvestment(null)
          }}
          investment={selectedInvestment}
        />
      )}

      {/* Tax Preview Modal — TAKARA */}
      {taxPreviewInvestment && (
        <TaxPreviewModal
          isOpen={isTaxPreviewOpen}
          onClose={() => {
            setIsTaxPreviewOpen(false)
            setTaxPreviewInvestment(null)
          }}
          onConfirm={handleConfirmTakaraClaim}
          amount={taxPreviewInvestment.availableRewardTAKARA}
          tokenSymbol="TAKARA"
          isPending={claimTAKARA.isPending}
          referralDeduction={taxPreviewInvestment.referralDeductionTAKARA}
        />
      )}

      {/* Tax Preview Modal — USDT */}
      {usdtPreviewInvestment && (
        <TaxPreviewModal
          isOpen={isUsdtPreviewOpen}
          onClose={() => {
            setIsUsdtPreviewOpen(false)
            setUsdtPreviewInvestment(null)
          }}
          onConfirm={handleConfirmUsdtClaim}
          amount={usdtPreviewInvestment.availableRewardUSDT}
          tokenSymbol="USDT"
          isPending={claimUSDT.isPending}
          referralDeduction={usdtPreviewInvestment.referralDeductionUSDT}
        />
      )}
    </div>
  )
}
